/* ============================================================
   Dathent — Auth screens (Login / Signup / Forgot / Reset / Confirm)
   White-first surface from the design pack (Auth.html). Same MFA
   logic and AuthContext API as the legacy screen — only the surface
   changes.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Icons as I } from '../components/pack-icons';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../services/api/client.js';
import ThemeToggle from '../components/AppShell/ThemeToggle';
import LangSwitch from '../components/AppShell/LangSwitch';

/* ---------------- Shell ---------------- */
function AuthShell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="auth-page">
      <header className="auth-topbar">
        <button
          className="auth-topbar-l"
          type="button"
          onClick={() => navigate('/login')}
          style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}
          aria-label="Dathent home"
        >
          <span className="brand-mark">D</span>
          <span>Dathent</span>
        </button>
        <div className="auth-topbar-r">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-card">{children}</div>
    </div>
  );
}

/* ---------------- Login ---------------- */
function LoginCard() {
  const { login, isAuthenticated, loading, error: ctxError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaSetupChallenge, setMfaSetupChallenge] = useState(null);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const from = location.state?.from?.pathname;
      // Default to /app (new AppShell) when there's no specific origin.
      // Sending users to "/" used to land them on the legacy MainApp.
      navigate(from && !['/login', '/signup', '/forgot', '/reset', '/confirm'].includes(from) ? from : '/app', { replace: true });
    }
  }, [isAuthenticated, loading, location.state, navigate]);

  async function submit(event) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requiresMfa) {
        setMfaChallenge(result);
        return;
      }
      if (result?.requiresMfaSetup) {
        setMfaSetupChallenge(result);
        return;
      }
      navigate('/app', { replace: true });
    } catch (err) {
      setFormError(err.message || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (mfaSetupChallenge) {
    return <MfaSetupCard challenge={mfaSetupChallenge} onBack={() => setMfaSetupChallenge(null)} />;
  }
  if (mfaChallenge) {
    return <MfaChallengeCard challenge={mfaChallenge} onBack={() => setMfaChallenge(null)} />;
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">Welcome back.</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>Sign in to your Dathent workspace.</p>
      </div>

      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="login-email">Work email</label>
          <input
            id="login-email"
            className="auth-field"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@brand.com"
            autoComplete="email"
            required
          />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor="login-password">
            <span>Password</span>
            <button type="button" className="linklike" onClick={() => navigate('/forgot')}>
              Forgot?
            </button>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="login-password"
              className="auth-field"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="current-password"
              minLength={8}
              required
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute', right: 8, top: 8,
                background: 'none', border: 0, color: 'var(--text-subtle)',
                padding: 6, cursor: 'pointer', display: 'inline-flex'
              }}
            >
              <I.eye size={16} />
            </button>
          </div>
        </div>
        {(formError || ctxError) && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{formError || ctxError}</span>
          </div>
        )}
        <button className="auth-btn violet block" type="submit" disabled={submitting || loading}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </div>

      <p className="auth-foot">
        New to Dathent?{' '}
        <button type="button" className="linklike" onClick={() => navigate('/signup')}>
          Create a workspace
        </button>
      </p>
    </form>
  );
}

/* ---------------- Signup (gated: early-access form) ----------------
   Public signup is paused while we onboard new workspaces manually.
   Existing users still sign in via the normal login flow. */
const EARLY_ACCESS_FORM_URL = 'https://forms.gle/7U4vLBGXRNM2Mv527';

function SignupCard() {
  const navigate = useNavigate();
  return (
    <>
      <div>
        <h2 className="auth-h">Early access only.</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          New Dathent accounts are currently invite-only. Existing users can still log in normally.
        </p>
      </div>

      <div className="auth-info">
        Need a workspace? Request early access and we'll enable your account when onboarding is ready.
      </div>

      <a
        className="auth-btn violet block"
        href={EARLY_ACCESS_FORM_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Request early access
      </a>

      <button
        className="auth-btn secondary block"
        type="button"
        onClick={() => navigate('/login')}
      >
        Back to sign in
      </button>
    </>
  );
}

/* ---------------- Forgot password ---------------- */
function ForgotCard() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.auth.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Unable to send reset link');
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <>
        <div className="auth-mail-badge">
          <I.send size={22} />
        </div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>Check your email.</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            We sent a reset link to <b style={{ color: 'var(--text)' }}>{email}</b>. It expires in 10 minutes.
          </p>
        </div>
        <div className="auth-info" style={{ textAlign: 'left' }}>
          Can't find it? Check spam, or click resend below.
        </div>
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <button className="auth-btn secondary" style={{ flex: 1 }} type="button" onClick={() => { setSent(false); setEmail(''); }}>
            Use a different email
          </button>
          <button className="auth-btn violet" style={{ flex: 1 }} type="button" onClick={submit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Resend link'}
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">Reset your password.</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          Enter your work email and we'll send a secure reset link.
        </p>
      </div>
      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="forgot-email">Work email</label>
          <input
            id="forgot-email"
            className="auth-field"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@brand.com"
            autoComplete="email"
            required
          />
        </div>
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}
        <button className="auth-btn violet block" type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </div>
      <p className="auth-foot">
        <button type="button" className="linklike" onClick={() => navigate('/login')}>
          ← Back to sign in
        </button>
      </p>
    </form>
  );
}

/* ---------------- Reset password ---------------- */
function ResetCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    if (!token) { setError('This reset link is missing a token. Request a new one.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await api.auth.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Unable to reset password');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <>
        <div className="auth-mail-badge"><I.check size={22} /></div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>Password updated.</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            Use the new password to sign in.
          </p>
        </div>
        <button className="auth-btn violet block" type="button" onClick={() => navigate('/login')}>
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">Create new password.</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          Choose a new password for your Dathent account.
        </p>
      </div>
      {!token && (
        <div className="auth-info">
          This reset link is missing a token. Request a new password reset email.
        </div>
      )}
      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            className="auth-field"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            minLength={8}
            autoComplete="new-password"
            required
          />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor="reset-confirm">Confirm password</label>
          <input
            id="reset-confirm"
            className="auth-field"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter password"
            minLength={8}
            autoComplete="new-password"
            required
          />
        </div>
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}
        <button className="auth-btn violet block" type="submit" disabled={submitting || !token}>
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </div>
      <p className="auth-foot">
        <button type="button" className="linklike" onClick={() => navigate('/login')}>
          ← Back to sign in
        </button>
      </p>
    </form>
  );
}

/* ---------------- Confirm email ---------------- */
function ConfirmCard() {
  const navigate = useNavigate();
  return (
    <>
      <div className="auth-mail-badge"><I.send size={22} /></div>
      <div>
        <h2 className="auth-h" style={{ textAlign: 'center' }}>Confirm your email.</h2>
        <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
          Open the confirmation link from your inbox, then return to Dathent.
        </p>
      </div>
      <button className="auth-btn violet block" type="button" onClick={() => navigate('/login')}>
        Back to sign in
      </button>
    </>
  );
}

/* ---------------- MFA challenge (post-password) ---------------- */
function MfaChallengeCard({ challenge, onBack }) {
  const { verifyMfaLogin, verifyMfaBackupCode, sendMfaEmailCode, verifyMfaEmailCode } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('totp');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'backup') {
        await verifyMfaBackupCode(challenge.attemptId, backupCode.trim());
      } else if (mode === 'email') {
        await verifyMfaEmailCode(challenge.attemptId, emailCode.trim());
      } else {
        await verifyMfaLogin(challenge.attemptId, code.trim());
      }
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not verify code');
    } finally {
      setSubmitting(false);
    }
  }

  async function requestEmailCode() {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await sendMfaEmailCode(challenge.attemptId);
      setEmailSent(true);
      setMode('email');
      setNotice(`We sent a code to ${challenge.emailHint || 'your email'}.`);
    } catch (err) {
      setError(err.message || 'Could not send email code');
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    (mode === 'totp' && code.length !== 6) ||
    (mode === 'email' && emailCode.length !== 6) ||
    (mode === 'backup' && !backupCode.trim());

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', background: 'var(--accent-soft)', color: 'var(--accent-text)',
        borderRadius: 999, alignSelf: 'flex-start', fontSize: 11.5, fontWeight: 500,
      }}>
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        {mode === 'backup' ? 'Recovery code' : mode === 'email' ? 'Email code' : 'Authenticator app'}
      </div>
      <div>
        <h2 className="auth-h">
          {mode === 'backup' ? 'Enter a recovery code.' : 'Enter your 6-digit code.'}
        </h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          {mode === 'backup'
            ? 'Each saved code works once.'
            : mode === 'email'
              ? `Code sent to ${challenge.emailHint || 'your email'}.`
              : 'Open your authenticator app and enter the code for Dathent.'}
        </p>
      </div>

      {mode === 'totp' && (
        <CodeInput6 value={code} onChange={setCode} autoFocus />
      )}
      {mode === 'email' && (
        <CodeInput6 value={emailCode} onChange={setEmailCode} autoFocus />
      )}
      {mode === 'backup' && (
        <div className="field-group">
          <label className="field-label" htmlFor="mfa-backup">Recovery code</label>
          <input
            id="mfa-backup"
            className="auth-field mono"
            type="text"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            placeholder="xxxx-xxxx-xxxx"
            autoFocus
          />
        </div>
      )}

      {notice && <div className="auth-info">{notice}</div>}
      {error && (
        <div className="auth-error">
          <I.alert size={14} />
          <span>{error}</span>
        </div>
      )}

      <button className="auth-btn violet block" type="submit" disabled={submitDisabled}>
        {submitting ? 'Verifying…' : 'Verify'}
      </button>

      <div className="resend-line" style={{ flexWrap: 'wrap', gap: 8 }}>
        {mode !== 'totp' && (
          <button type="button" className="linklike" onClick={() => { setMode('totp'); setError(null); setNotice(null); }}>
            Use authenticator
          </button>
        )}
        {mode !== 'backup' && (
          <button type="button" className="linklike" onClick={() => { setMode('backup'); setError(null); setNotice(null); }}>
            Use recovery code
          </button>
        )}
        {challenge.emailCodeAvailable && (
          <button type="button" className="linklike" onClick={requestEmailCode} disabled={submitting}>
            {emailSent ? 'Resend email code' : 'Email me a code'}
          </button>
        )}
        <button type="button" className="linklike" style={{ color: 'var(--text-subtle)' }} onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}

/* ---------------- MFA setup (forced enrollment) ---------------- */
function MfaSetupCard({ challenge, onBack }) {
  const { verifyRequiredMfaSetup, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [codesSaved, setCodesSaved] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!challenge?.otpauthUrl) return;
    QRCode.toDataURL(challenge.otpauthUrl, { width: 168, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [challenge]);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    if (backupCodes) {
      if (!codesSaved) { setError('Save your backup codes before continuing.'); return; }
      await reloadUser();
      navigate('/app', { replace: true });
      return;
    }
    if (!/^\d{6}$/.test(code)) { setError('Enter a valid 6-digit code.'); return; }
    setSubmitting(true);
    try {
      const result = await verifyRequiredMfaSetup(challenge.attemptId, code);
      setBackupCodes(result.backupCodes || []);
    } catch (err) {
      setError(err.message || 'Could not enable 2FA');
    } finally {
      setSubmitting(false);
    }
  }

  if (backupCodes) {
    return (
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <div>
          <h2 className="auth-h">Save these recovery codes.</h2>
          <p className="auth-sub" style={{ marginTop: 6 }}>
            Each works once. Store them somewhere safe — they're the only way back if you lose access to your authenticator.
          </p>
        </div>
        <div className="auth-success">
          <b>Two-factor is enabled.</b> Your next sign-in will ask for a code.
        </div>
        <div className="recovery-codes">
          {backupCodes.map((c) => <code key={c}>{c}</code>)}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="auth-btn secondary" style={{ flex: 1 }}
            onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))}>
            <I.copy size={14} /> Copy
          </button>
          <button type="button" className="auth-btn secondary" style={{ flex: 1 }}
            onClick={() => {
              const blob = new Blob([backupCodes.join('\n') + '\n'], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'dathent-backup-codes.txt';
              link.click();
              URL.revokeObjectURL(url);
            }}>
            <I.download size={14} /> Download
          </button>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={codesSaved} onChange={(e) => setCodesSaved(e.target.checked)}
                 style={{ accentColor: 'var(--accent)', width: 16, height: 16, marginTop: 2 }} />
          <span>I have saved these codes.</span>
        </label>
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}
        <button type="submit" className="auth-btn violet block" disabled={!codesSaved}>
          Enter Dathent
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">Set up two-factor.</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          Scan the QR with your authenticator app, then enter the first 6-digit code to confirm.
        </p>
      </div>
      <div className="auth-qr">
        {qrDataUrl
          ? <img src={qrDataUrl} alt="Authenticator QR code" />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-subtle)', fontSize: 12 }}>Loading…</div>}
      </div>
      {challenge.manualKey && (
        <details style={{ fontSize: 12.5, color: 'var(--text-subtle)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--accent-text)' }}>Can't scan? Show setup key</summary>
          <div style={{ marginTop: 8, padding: 8, background: 'var(--bg-subtle)', borderRadius: 'var(--r-md)', wordBreak: 'break-all', fontFamily: 'var(--font-mono)' }}>
            {challenge.manualKey}
          </div>
        </details>
      )}
      <div className="field-group">
        <label className="field-label">First 6-digit code</label>
        <CodeInput6 value={code} onChange={setCode} autoFocus />
      </div>
      {error && (
        <div className="auth-error">
          <I.alert size={14} />
          <span>{error}</span>
        </div>
      )}
      <button type="submit" className="auth-btn violet block" disabled={submitting || code.length !== 6}>
        {submitting ? 'Verifying…' : 'Confirm & enable'}
      </button>
      <p className="auth-foot">
        <button type="button" className="linklike" onClick={onBack}>← Back to sign in</button>
      </p>
    </form>
  );
}

/* ---------------- 6-digit code input ---------------- */
function CodeInput6({ value, onChange, autoFocus }) {
  const refs = useRef([]);
  const chars = (value || '').padEnd(6, ' ').slice(0, 6).split('');

  const setChar = (i, ch) => {
    const next = (value || '').padEnd(6, ' ').split('');
    next[i] = ch;
    onChange(next.join('').replace(/ /g, ''));
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !e.currentTarget.value && i > 0) {
      refs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
  };

  const onInput = (i, e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
    setChar(i, v);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (raw) {
      e.preventDefault();
      onChange(raw);
      refs.current[Math.min(5, raw.length - 1)]?.focus();
    }
  };

  return (
    <div className="code-input">
      {chars.map((ch, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          maxLength={1}
          inputMode="numeric"
          pattern="[0-9]*"
          value={ch === ' ' ? '' : ch}
          className={ch !== ' ' ? 'has-value' : ''}
          onChange={(e) => onInput(i, e)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          autoFocus={autoFocus && i === 0}
        />
      ))}
    </div>
  );
}

/* ---------------- Public entry ---------------- */
export const Auth = ({ mode = 'login' }) => {
  return (
    <AuthShell>
      {mode === 'login' && <LoginCard />}
      {mode === 'signup' && <SignupCard />}
      {mode === 'forgot' && <ForgotCard />}
      {mode === 'reset' && <ResetCard />}
      {mode === 'confirm' && <ConfirmCard />}
    </AuthShell>
  );
};

export default Auth;
